import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';

interface Cell {
	code: string;
	label: string;
	colspan?: number;
}

interface Row {
	tiefe: string;
	bereich: string;
	cells: Cell[];
}

const E: Cell = { code: '', label: '' };

@Component({
	standalone: true,
	imports: [CommonModule, NgbNavModule],
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Grund- und Stauwasserformen</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<ul ngbNav #nav="ngbNav" [(activeId)]="activeTab" class="nav-tabs mb-3">
				<li [ngbNavItem]="'grund'">
					<button ngbNavLink>Grundwasserformen</button>
					<ng-template ngbNavContent>
						<ng-container *ngTemplateOutlet="tabContent; context: { $implicit: 'grund' }"></ng-container>
					</ng-template>
				</li>
				<li [ngbNavItem]="'stau'">
					<button ngbNavLink>Stauwasserformen</button>
					<ng-template ngbNavContent>
						<ng-container *ngTemplateOutlet="tabContent; context: { $implicit: 'stau' }"></ng-container>
					</ng-template>
				</li>
			</ul>
			<div [ngbNavOutlet]="nav"></div>
		</div>

		<ng-template #tabContent let-m>
			<div style="overflow-x: auto; -webkit-overflow-scrolling: touch;">
			<!-- Tabelle 1: Überwässerung / Überflutung -->
			<h6 class="mt-0 mb-1">Überwässerung / Überflutung</h6>
			<table class="table table-sm table-bordered matrix-table mb-3">
				<thead>
					<tr>
						<th rowspan="2" class="th-tiefe">Frühjährl.<br>Tiefenstufe ³⁾</th>
						<th [attr.colspan]="ueberwCols(m).length" class="text-center small">
							jahreszeitliche Andauerstufe<br>
							nach der Andauer der <strong>Überwässerung</strong> bzw. <strong>Überflutung</strong> ⁴⁾ in Monaten ⁵⁾
						</th>
					</tr>
					<tr>
						<th *ngFor="let col of ueberwCols(m)" class="th-col text-center">{{ col }}</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let row of ueberwData(m)">
						<td class="td-tiefe"><strong>{{ row.tiefe }}</strong></td>
						<ng-container *ngFor="let cell of row.cells">
							<td [attr.colspan]="cell.colspan || 1" class="td-cell text-center" [class.empty]="!cell.code">
								<ng-container *ngIf="cell.code">
									<span class="cell-label">{{ cell.label }}</span><br>
									<strong class="cell-code">{{ cell.code }}</strong>
								</ng-container>
							</td>
						</ng-container>
					</tr>
				</tbody>
			</table>

			<!-- Tabelle 2: Absink / Austrocknung -->
			<h6 class="mb-1">{{ m === 'grund' ? 'Grundwasser-Absinkstufen' : 'Austrocknung des Bodens' }}</h6>
			<table class="table table-sm table-bordered matrix-table mb-3">
				<thead>
					<tr>
						<th rowspan="2" class="th-tiefe">Frühjährl.<br>Tiefenstufe ³⁾</th>
						<th [attr.colspan]="table2Cols(m).length" class="text-center small" [innerHTML]="table2Hdr(m)"></th>
					</tr>
					<tr>
						<th *ngFor="let col of table2Cols(m)" class="th-col text-center">{{ col }}</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let row of table2Data(m)">
						<td class="td-tiefe">
							<strong>{{ row.tiefe }}</strong>
							<br *ngIf="row.bereich">
							<span *ngIf="row.bereich" class="text-muted small">{{ row.bereich }}</span>
						</td>
						<ng-container *ngFor="let cell of row.cells">
							<td [attr.colspan]="cell.colspan || 1" class="td-cell text-center" [class.empty]="!cell.code">
								<ng-container *ngIf="cell.code">
									<span class="cell-label">{{ cell.label }}</span><br>
									<strong class="cell-code">{{ cell.code }}</strong>
								</ng-container>
							</td>
						</ng-container>
					</tr>
				</tbody>
			</table>

			<!-- Fußnoten -->
			<div class="footnotes text-muted small">
				<div *ngFor="let fn of fns(m)">{{ fn }}</div>
			</div>
			<p class="text-muted small mt-2 mb-0">{{ m === 'grund' ? quelleGrund : quelleStau }}</p>
			</div>
		</ng-template>
	`,
	styles: [`
		.matrix-table {
			font-size: 0.8rem;
			min-width: 550px;
		}
		.th-tiefe, .td-tiefe {
			white-space: nowrap;
			vertical-align: middle;
			background-color: #f8f9fa;
		}
		.th-col {
			font-size: 0.75rem;
			min-width: 70px;
			vertical-align: middle;
		}
		.td-cell {
			vertical-align: middle;
			padding: 0.3rem 0.25rem;
		}
		.td-cell.empty {
			background-color: #f8f9fa;
		}
		.cell-label {
			font-size: 0.72rem;
			line-height: 1.2;
		}
		.cell-code {
			font-size: 0.9rem;
		}
		.footnotes div {
			margin-bottom: 0.15rem;
		}
	`]
})
export class WasserformInfoModalComponent {
	@Input() activeTab: 'grund' | 'stau' = 'grund';

	quelleGrund = 'Quelle: SEA 95, Teil A, Darst. A 3\u20132a, S. 111';
	quelleStau = 'Quelle: SEA 95, Teil A, Darst. A 3\u20134a, S. 116\u2013117';

	activeModal = inject(NgbActiveModal);

	// --- Dispatcher-Methoden für Template ---

	ueberwCols(m: string): string[] {
		return m === 'grund'
			? ['>10', '8…10', '6…8', '4…6', '2…4', '1…2', '<1']
			: ['8…10', '6…8', '4…6', '2…4', '1…2', '<1'];
	}

	ueberwData(m: string): Row[] {
		return m === 'grund' ? this.grundUeberw : this.stauUeberw;
	}

	table2Cols(m: string): string[] {
		return m === 'grund'
			? ['0', '1', '2', '3', '4', '>4']
			: ['um 2', 'um 4', 'um 6', 'um 8', '>8'];
	}

	table2Hdr(m: string): string {
		return m === 'grund'
			? 'jahreszeitliche Absinkstufe des <strong>Grundwassers</strong><br>bei spätsommerlich-herbstlichem Tiefstand absinkend um Tiefenstufen ⁶⁾'
			: 'jahreszeitliche Andauerstufe<br>nach der Andauer der <strong>Austrocknung</strong> des Bodens in Monaten';
	}

	table2Data(m: string): Row[] {
		return m === 'grund' ? this.grundAbsink : this.stauAustrocknung;
	}

	fns(m: string): string[] {
		return m === 'grund' ? this.grundFootnotes : this.stauFootnotes;
	}

	// ==================== GRUNDWASSERFORMEN ====================

	private grundUeberw: Row[] = [
		{
			tiefe: 'oberhalb Flur', bereich: '',
			cells: [
				{ code: '11', label: 'ständig überwässert bzw. überflutet ³⁾' },
				{ code: '12', label: 'langzeitig überwässert bzw. überflutet ³⁾' },
				{ code: '13', label: 'halbzeitig überwässert bzw. überflutet ³⁾' },
				{ code: '14', label: 'kz. stark' },
				{ code: '15', label: 'kz. mäßig' },
				{ code: '16', label: 'kz. zieml. gering' },
				{ code: '17', label: 'kz. gering' },
			]
		},
	];

	private grundAbsink: Row[] = [
		{
			tiefe: 'um 0,1', bereich: '(0 … 0,2)',
			cells: [
				{ code: '22', label: 'ständig grundsumpfig (schwamm-/schwingsumpfig)' },
				{ code: '23', label: 'langzeitig grundsumpfig' },
				{ code: '24', label: 'halbzeitig grundsumpfig' },
				{ code: '25', label: 'kz. stark' },
				{ code: '26', label: 'kz. mäßig' },
				{ code: '27', label: 'kz. zieml. gering / gering grundsumpfig' },
			]
		},
		{
			tiefe: 'um 0,35', bereich: '(>0,2 … 0,5)',
			cells: [
				{ code: '33', label: 'ständig grundwasser\u00ADbeherrscht' },
				{ code: '34', label: 'langzeitig grundwasser\u00ADbeherrscht' },
				{ code: '35', label: 'halbzeitig grundwasser\u00ADbeherrscht' },
				{ code: '36', label: 'kz. stark', colspan: 2 },
				{ code: '37', label: 'kz. mäßig grundwasserbeherrscht' },
			]
		},
		{
			tiefe: 'um 0,75', bereich: '(>0,5 … 1,0)',
			cells: [
				{ code: '44', label: 'ständig grundwasser\u00ADnah' },
				{ code: '45', label: 'langzeitig grundwasser\u00ADnah' },
				{ code: '46', label: 'halbzeitig grundwasser\u00ADnah' },
				{ code: '47', label: 'kurzzeitig grundwassernah', colspan: 3 },
			]
		},
		{
			tiefe: 'um 1,4', bereich: '(>1,0 … 1,8)',
			cells: [
				{ code: '55', label: 'ständig grundwasser\u00ADbeeinflusst' },
				{ code: '56', label: 'langzeitig grundwasser\u00ADbeeinflusst' },
				{ code: '57', label: 'halbzeitig grundwasserbeeinflusst', colspan: 4 },
			]
		},
		{
			tiefe: 'um 2,4', bereich: '(>1,8 … 3,0)',
			cells: [
				E,
				{ code: '67', label: 'schwach grundwasserbeeinflusst', colspan: 5 },
			]
		},
		{
			tiefe: '>3,0', bereich: '',
			cells: [
				{ code: '7', label: 'grundwasserfern', colspan: 6 },
			]
		},
	];

	private grundFootnotes: string[] = [
		'¹⁾ Kombination von Grund- und Stauwasserformen (bei Amphigleyen) werden im Ziffernsymbol durch einen Schrägstrich getrennt, die Grundwasserstufe steht hinter dem Schrägstrich.',
		'²⁾ Bei Neigung der Grundwasseroberfläche über 3° wird die Silbe „grund" durch „hang" ersetzt und das Ziffernsymbol erhält ein Pluszeichen, z.B. 3⁺6 = kurzzeitig stark hangwasserbeherrscht.',
		'³⁾ Frühjährliche Tiefenstufe nach langjährigem Mittel des Hochstandes in m Spiegeltiefe.',
		'⁴⁾ „Überflutet" bei durch Fließgewässer herangeführtem fremdbürtigem Wasser. Das Ziffernsymbol erhält ein hochgestelltes liegendes Kreuz, z.B. 1ˣ4 = kurzzeitig stark überflutet.',
		'⁵⁾ Ohne Anrechnung von Schneewasser über gefrorenem Boden. Episodische Überflutungen werden wie vergleichbare schwächere periodische gewertet.',
		'⁶⁾ Es gelten dabei die Tiefenspannen der frühjährlichen Tiefenstufen. Beispiel: Wasserstufe 56 bedeutet: Das Grundwasser kann im Frühjahr (März–Mai) zwischen 1,00 m und 1,80 m Tiefe schwanken. Im Herbst (August–Oktober) sinkt es ab und kann zwischen 1,80 m und 3,00 m schwanken.',
		'Form 7 (grundwasserfern) kann eingetragen werden, wird aber nicht in der Beschriftung angezeigt.',
	];

	// ==================== STAUWASSERFORMEN ====================

	private stauUeberw: Row[] = [
		{
			tiefe: 'oberhalb Flur', bereich: '',
			cells: [
				{ code: '12', label: 'langzeitig überwässert bzw. überflutet' },
				{ code: '13', label: 'halbzeitig überwässert bzw. überflutet' },
				{ code: '14', label: 'kz. stark' },
				{ code: '15', label: 'kz. mäßig' },
				{ code: '16', label: 'kz. zieml. gering' },
				{ code: '17', label: 'kz. gering' },
			]
		},
	];

	private stauAustrocknung: Row[] = [
		{
			tiefe: 'um 0,1', bereich: '(0 … 0,2)',
			cells: [
				{ code: '23', label: 'langzeitig stausumpfig' },
				{ code: '24', label: 'halbzeitig stausumpfig' },
				{ code: '25', label: 'kz. stark' },
				{ code: '26', label: 'kz. mäßig' },
				{ code: '27', label: 'kz. zieml. gering stausumpfig' },
			]
		},
		{
			tiefe: 'um 0,3', bereich: '(>0,2 … 0,4)',
			cells: [
				{ code: '34', label: 'langzeitig stauwasser\u00ADbeherrscht' },
				{ code: '35', label: 'halbzeitig stauwasser\u00ADbeherrscht' },
				{ code: '36', label: 'kz. stark' },
				{ code: '37', label: 'kz. mäßig stauwasserbeherrscht', colspan: 2 },
			]
		},
		{
			tiefe: 'um 0,6', bereich: '(>0,4 … 0,8)',
			cells: [
				E,
				{ code: '46', label: 'halbzeitig stauwassernah' },
				{ code: '47', label: 'kurzzeitig stauwassernah', colspan: 3 },
			]
		},
	];

	private stauFootnotes: string[] = [
		'¹⁾ Kombinationen von Stau- und Grundwasserformen (bei Amphigleyen) werden im Ziffernsymbol durch einen Schrägstrich getrennt; die Grundwasserstufe steht hinter dem Schrägstrich.',
		'²⁾ Bei Neigung der Stauwasseroberfläche über 3° wird die Silbe „hang" vorgesetzt und das Symbol erhält ein Pluszeichen, z.B. 3⁺6 = kurzzeitig stark hangstauwasserbeherrscht.',
		'³⁾ Frühjährliche Tiefenstufe nach dem langjährigen Mittel des Hochstandes in m Spiegeltiefe.',
		'⁴⁾ „Überflutet" bei durch Fließgewässer herangeführtem fremdbürtigem Wasser. Das Ziffernsymbol erhält ein hochgestelltes liegendes Kreuz, z.B. 1ˣ4 = kurzzeitig stark überflutet.',
		'⁵⁾ Ohne Anrechnung von Schneewasser über gefrorenem Boden. Episodische Überflutungen werden wie vergleichbare schwächere periodische gewertet.',
	];
}
